import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { LoginBody } from '@tasks/contracts';

import { login } from '../../api/auth';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Logo } from '../../components/Logo';
import { useAuth } from '../../auth/AuthProvider';
import { isApiError } from '../../lib/errors';
import styles from './LoginPage.module.css';

/** Форма входа: центрированная карточка 380 px (docs/06 §3.5). */
export function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isLoading } = useAuth();
  const [form, setForm] = useState<LoginBody>({ login: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const next = searchParams.get('next') ?? '/';

  const mutation = useMutation({
    mutationFn: (body: LoginBody) => login(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
      navigate(next, { replace: true });
    },
    onError: (cause: unknown) => {
      const code = isApiError(cause) ? cause.code : 'UNKNOWN';
      setErrorCode(code);
      if (code === 'RATE_LIMITED') {
        setError('Слишком много попыток входа. Повторите через 15 минут.');
        return;
      }
      setError('Неверный логин или пароль');
    },
  });

  if (!isLoading && user && !user.mustChangePassword) {
    return <Navigate to={next} replace />;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    setErrorCode(null);
    if (form.login.trim().length === 0) {
      setError('Введите логин');
      return;
    }
    if (form.password.length === 0) {
      setError('Введите пароль');
      return;
    }
    mutation.mutate({ login: form.login.trim(), password: form.password });
  };

  return (
    <main className={styles['screen']}>
      <form className={styles['card']} onSubmit={handleSubmit} noValidate>
        <div className={styles['brand']}>
          <Logo size="lg" />
          <h1 className={styles['title']}>Вход</h1>
          <p className={styles['subtitle']}>Войдите, чтобы продолжить работу над задачами</p>
        </div>

        <Input
          label="Логин"
          name="login"
          autoComplete="username"
          autoFocus
          value={form.login}
          onChange={(event) => setForm((prev) => ({ ...prev, login: event.target.value }))}
        />
        <Input
          label="Пароль"
          name="password"
          type="password"
          autoComplete="current-password"
          value={form.password}
          onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
        />

        {error ? (
          <div className={styles['error']} role="alert">
            <p>{error}</p>
            {errorCode ? <p className={styles['code']}>Код: {errorCode}</p> : null}
          </div>
        ) : null}

        <Button type="submit" variant="primary" fullWidth loading={mutation.isPending}>
          Войти
        </Button>
      </form>
    </main>
  );
}
